/**
 * SPA routing and view transitions.
 * Routes: home (/), portfolio (/portfolio, /portfolio/:section)
 */

const VT_CLASSES = [
  'transition-exit-left',
  'transition-exit-right',
  'transition-enter-from-right',
  'transition-enter-from-left',
];

const PORTFOLIO_SECTIONS = ['systems', 'craft', 'intelligence'];

let homePageInitialized = false;
let portfolioPageInitialized = false;

/**
 * Convert pathname to route.
 * @param {string} pathname
 * @returns {'home'|'portfolio'}
 */
export function pathnameToRoute(pathname) {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/portfolio' || p.startsWith('/portfolio/')) return 'portfolio';
  return 'home';
}

/**
 * Derive portfolio section from pathname.
 * Supports /portfolio and /portfolio/:section paths.
 * @param {string} pathname
 * @returns {'systems'|'craft'|'intelligence'}
 */
export function getPortfolioSectionFromPath(pathname) {
  const p = pathname.replace(/\/$/, '') || '/';
  const match = /^\/portfolio(?:\/([^/]+))?/.exec(p);
  const maybeSection = match && match[1];
  if (maybeSection && PORTFOLIO_SECTIONS.includes(maybeSection)) {
    return maybeSection;
  }
  return 'systems';
}

function showPage(route) {
  const home = document.getElementById('page-home');
  const portfolio = document.getElementById('page-portfolio');
  const showMoreBtn = document.getElementById('showMoreBtn');
  if (route === 'portfolio') {
    home?.classList.add('page-hidden');
    portfolio?.classList.remove('page-hidden');
    if (showMoreBtn) showMoreBtn.style.display = 'none';
  } else {
    portfolio?.classList.add('page-hidden');
    home?.classList.remove('page-hidden');
    if (showMoreBtn) showMoreBtn.style.display = '';
  }
  document.title =
    route === 'portfolio'
      ? 'Portfolio / Jonathan Kurten'
      : 'Jonathan Kurten / Design';
}

function resetScrollForRoute() {
  window.scrollTo(0, 0);
}

/**
 * Create the ensureTargetInitialized function for routing.
 * @param {() => void} initHome - Called when home page is first visited
 * @param {() => void} initPortfolio - Called when portfolio page is first visited
 * @returns {function(string): void} ensureTargetInitialized
 */
export function setPageInitializers(initHome, initPortfolio) {
  return function ensureTargetInitialized(route) {
    if (route === 'portfolio' && !portfolioPageInitialized) {
      initPortfolio();
      portfolioPageInitialized = true;
    }
    if (route === 'home' && !homePageInitialized) {
      initHome();
      homePageInitialized = true;
    }
  };
}

/**
 * Navigate to route with optional view transition.
 * @param {'home'|'portfolio'} to
 * @param {boolean} pushState
 * @param {function(string): void} ensureInit
 */
function transitionTo(to, pushState, ensureInit) {
  ensureInit(to);
  const exitLeft = to === 'portfolio';
  const root = document.documentElement;

  if (typeof document.startViewTransition === 'function') {
    root.classList.add(
      exitLeft ? 'transition-exit-left' : 'transition-exit-right',
      exitLeft ? 'transition-enter-from-right' : 'transition-enter-from-left',
    );
    document
      .startViewTransition(() => {
        showPage(to);
        resetScrollForRoute();
        if (pushState) {
          history.pushState(
            { route: to },
            '',
            to === 'portfolio' ? '/portfolio' : '/',
          );
        }
      })
      .finished.finally(() => root.classList.remove(...VT_CLASSES));
  } else {
    showPage(to);
    resetScrollForRoute();
    if (pushState) {
      history.pushState(
        { route: to },
        '',
        to === 'portfolio' ? '/portfolio' : '/',
      );
    }
  }
}

/**
 * Set up SPA navigation: intercept nav-to-portfolio / nav-to-home links, handle popstate.
 * @param {function(string): void} ensureInit - ensureTargetInitialized callback
 */
export function setupViewTransitionNavigation(ensureInit) {
  document.addEventListener('click', (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    const toPortfolio = e.target.closest('a.nav-to-portfolio');
    if (toPortfolio) {
      e.preventDefault();
      transitionTo('portfolio', true, ensureInit);
      return;
    }
    const toHome = e.target.closest('a.nav-to-home');
    if (toHome) {
      e.preventDefault();
      transitionTo('home', true, ensureInit);
      return;
    }
    const sectionLink = e.target.closest(
      'a.nav-popover-section[data-section]',
    );
    if (sectionLink) {
      e.preventDefault();
      const section = sectionLink.getAttribute('data-section') || 'systems';
      transitionTo('portfolio', false, ensureInit);
      const path =
        section === 'systems' ? '/portfolio' : `/portfolio/${section}`;
      history.pushState({ route: 'portfolio', section }, '', path);
      document.dispatchEvent(
        new CustomEvent('portfolio-section-change', { detail: { section } }),
      );
      return;
    }
  });

  window.addEventListener('popstate', () => {
    const to = pathnameToRoute(location.pathname);
    transitionTo(to, false, ensureInit);
    if (to === 'portfolio') {
      const section = getPortfolioSectionFromPath(location.pathname);
      document.dispatchEvent(
        new CustomEvent('portfolio-section-change', { detail: { section } }),
      );
    }
  });
}

/**
 * Bootstrap: initialize page and show it (for initial load).
 * @param {'home'|'portfolio'} route
 * @param {function(string): void} ensureInit
 */
export function bootstrapRoute(route, ensureInit) {
  ensureInit(route);
  showPage(route);
}
